import React, { useState, useRef, useEffect } from 'react';
import Form from './Form';
import styles from './SecureForm.module.css';

interface SecureFormField {
  field_id: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'url';
  placeholder?: string | null;
  required?: boolean;
  validation_pattern?: string | null;
}

interface SecureFormProps {
  formId: string;
  form_id?: string; // Alternative field name for backward compatibility
  title: string;
  description: string;
  fields: SecureFormField[];
  onSubmit: (formId: string, formData: Record<string, any>) => void;
  isLoading?: boolean;
}

const SecureForm: React.FC<SecureFormProps> = ({
  formId,
  form_id,
  title,
  description,
  fields,
  onSubmit,
  isLoading = false
}) => {
  // Use form_id if formId is not provided
  const actualFormId = formId || form_id || '';
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
      firstInputRef.current.select();
    }
  }, []);

  const handleInputChange = (fieldId: string, fieldLabel: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when user starts typing
    if (errors[fieldLabel]) {
      setErrors(prev => ({ ...prev, [fieldLabel]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    fields.forEach(field => {
      const fieldId = field.field_id;
      const value = formData[fieldId] || '';
      
      if (field.required !== false && !value.trim()) {
        newErrors[field.label] = `${field.label} is required`;
        return;
      }

      if (value.trim()) {
        switch (field.type) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              newErrors[field.label] = 'Please enter a valid email address';
            }
            break;
          case 'phone':
            const phoneRegex = /^[+]?[(]?[+]?\d{3}[)]?[-\s.]?\d{3}[-\s.]?\d{4,6}$/;
            if (!phoneRegex.test(value.replace(/\s/g, ''))) {
              newErrors[field.label] = 'Please enter a valid phone number';
            }
            break;
          case 'url':
            try {
              new URL(value);
            } catch {
              newErrors[field.label] = 'Please enter a valid URL';
            }
            break;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(actualFormId, formData);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, fieldIndex: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };


  const securityIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Main body of the lock */}
      <rect x="5" y="11" width="14" height="10" rx="2" fill="#3b82f6"/>
      {/* Top shackle of the lock */}
      <path d="M12 4C9.79086 4 8 5.79086 8 8V11H16V8C16 5.79086 14.2091 4 12 4Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Keyhole detail */}
      <circle cx="12" cy="15" r="2" fill="#1e40af"/>
      <rect x="11.5" y="17" width="1" height="3" fill="#1e40af"/>
    </svg>
  );

  return (
    <Form
      title={title}
      description={description}
      icon={securityIcon}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      submitButtonText="Submit Securely"
    >
      <div className={styles.formFields}>
        {fields.map((field, index) => {
          const fieldId = field.field_id;
          return (
            <div key={fieldId} className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>
                {field.label}
                {field.required !== false && <span className={styles.required}>*</span>}
              </label>
              <input
                ref={index === 0 ? firstInputRef : undefined}
                data-field-index={index}
                type={field.type === 'phone' ? 'tel' : field.type}
                value={formData[fieldId] || ''}
                onChange={(e) => handleInputChange(fieldId, field.label, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`${styles.fieldInput} ${errors[field.label] ? styles.fieldError : ''}`}
                placeholder={field.placeholder || getPlaceholder(field)}
                disabled={isLoading}
              />
              {errors[field.label] && (
                <span className={styles.errorMessage}>{errors[field.label]}</span>
              )}
            </div>
          );
        })}
      </div>
    </Form>
  );
};

function getPlaceholder(field: SecureFormField): string {
  switch (field.type) {
    case 'email':
      return 'your.email@example.com';
    case 'phone':
      return '(555) 123-4567';
    case 'url':
      return 'https://linkedin.com/in/yourname';
    default:
      return `Enter your ${field.label.toLowerCase()}`;
  }
}

export default SecureForm;